/**
 * The wagered mutator pool — fixed, public, and published at LOBBY CREATION.
 *
 * This is the file that converts a random event into a known condition. A player
 * who picks MONOLITH can read this pool before character select and price the
 * risk that the floor turns to ice; a player who picks MONOLITH and then loses
 * to a mid-match roll they could not have anticipated was beaten by randomness,
 * and committing the seed does not fix that. Committing the seed proves the roll
 * was not *authored* against them. Publishing the pool is what makes it *fair*.
 *
 * Every spec carries a measured per-archetype win-rate delta and the pool is
 * filtered on |delta| <= 5 percentage points (PRD §8 rule 10 / GAME-DESIGN §4
 * rule 2). The cap is asserted at module load, so a balance edit that pushes a
 * mutator out of tolerance fails at boot rather than at the first payout.
 *
 * The deltas below are the launch estimates from the design docs' own reasoning,
 * flagged as such. They are replaced by measured values from match telemetry —
 * that is the point of storing them as data rather than as a comment.
 */
import { assertChaosMutation, EXCLUDED_MUTATION_REASONS, isApplicableMutationKind } from "./mutations.js";
export const MAX_ABS_ARCHETYPE_DELTA_PCT = 5;
const flat = (v) => ({
    assassin: v,
    juggernaut: v,
    controller: v,
    trickster: v,
    artillery: v,
});
const delta = (d) => ({ ...flat(0), ...d });
export const MUTATOR_SPECS = {
    low_gravity: {
        kind: "low_gravity",
        tier: "mutator",
        label: "LOW GRAVITY",
        description: "Everything falls slower. Jumps carry further, landings take longer.",
        archetypeDeltaPct: delta({ artillery: 3, juggernaut: -3, assassin: 1 }),
        deltaSource: "estimate",
        wageredLegal: true,
        build: (rng) => ({
            kind: "low_gravity",
            gravityPct: rng.int(45, 66),
            durationMs: rng.int(6, 11) * 5_000,
        }),
    },
    ice_floor: {
        kind: "ice_floor",
        tier: "mutator",
        label: "ICE FLOOR",
        description: "The ground stops gripping. Everyone slides.",
        archetypeDeltaPct: delta({ juggernaut: -4, trickster: 2, controller: 3 }),
        deltaSource: "estimate",
        wageredLegal: true,
        build: (rng) => ({
            kind: "ice_floor",
            durationMs: rng.int(6, 11) * 5_000,
        }),
    },
    conveyor_floor: {
        kind: "conveyor_floor",
        tier: "mutator",
        label: "DRIFT",
        description: "The arena floor moves in one direction. Standing still is no longer standing still.",
        archetypeDeltaPct: delta({ juggernaut: -2, assassin: 2, artillery: -1 }),
        deltaSource: "estimate",
        wageredLegal: true,
        build: (rng) => ({
            kind: "conveyor_floor",
            directionDeg: rng.int(0, 360),
            // Units/sec of steady drift. The sim caps this below the ground
            // deceleration per tick; above that the drift diverges instead of
            // settling. See MUTATION_BOUNDS in @chaos/sim.
            driftSpeed: rng.float(0.8, 1.85),
            durationMs: rng.int(5, 9) * 5_000,
        }),
    },
    hft_belt_flow: {
        kind: "hft_belt_flow",
        tier: "mutator",
        label: "ORDER FLOW",
        description: "Local HFT review only: one authored belt route reverses or surges.",
        archetypeDeltaPct: flat(0),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: "HFT-only mechanics require arena capability gating and balance review",
        build: (rng) => ({
            kind: "hft_belt_flow",
            routeGroup: rng.int(0, 3),
            directionMultiplier: rng.int(0, 2) === 0 ? -1 : 1,
            strengthPct: rng.int(100, 201),
            durationMs: rng.int(1, 5) * 5_000,
        }),
    },
    speed_surge: {
        kind: "speed_surge",
        tier: "mutator",
        label: "OVERCLOCK",
        description: "Everyone moves faster. Fights start and end sooner.",
        archetypeDeltaPct: delta({ assassin: 3, artillery: -2, juggernaut: -2 }),
        deltaSource: "estimate",
        wageredLegal: true,
        build: (rng) => ({
            kind: "speed_surge",
            speedPct: rng.int(112, 126),
            durationMs: rng.int(4, 8) * 5_000,
        }),
    },
    // --- excluded, with the reason recorded as data ---------------------------
    //
    // Two different kinds of exclusion live below, and the difference matters:
    //
    //   BALANCE  — the simulation implements it and a casual lobby can roll it,
    //              but its archetype spread is outside the +/-5pp cap so a wagered
    //              match may not (lava_floor, cooldown_haste).
    //   CAPABILITY — the simulation cannot express it at all, so it is not a
    //              mutation any lobby can roll. `ChaosMutation` does not include
    //              these shapes; the spec survives to record the measurement and
    //              the reason (see EXCLUDED_MUTATION_REASONS in mutations.ts).
    //
    // A pool entry the sim silently ignores is worse than a missing one: it is
    // published to players before character select as a risk they should price.
    inverted_controls: {
        kind: "inverted_controls",
        tier: "mutator",
        label: "INVERTED CONTROLS",
        description: "Your movement is reversed. Your aim is not.",
        // Movement-only inversion, so nobody loses the ability to fight back — the
        // reason this is inside the cap at all. The spread is about who NEEDS
        // precise footwork: an assassin closing distance and a juggernaut holding
        // ground both pay for it, artillery standing still and aiming barely does.
        archetypeDeltaPct: delta({ assassin: -4, juggernaut: -2, artillery: 3 }),
        deltaSource: "estimate",
        wageredLegal: true,
        build: (rng) => ({
            kind: "inverted_controls",
            // Short. Ten seconds is a scramble, ninety is just bad to play.
            durationMs: rng.int(2, 4) * 5_000,
        }),
    },
    player_scale: {
        kind: "player_scale",
        tier: "mutator",
        label: "SIZE SHIFT",
        description: "Everyone grows or shrinks. The hitbox comes with it.",
        // CASUAL ONLY, and honestly so. Body size changes how easy a player is to
        // hit at range, so the delta is real — but its SIGN flips with the roll:
        // grown bodies favour artillery, shrunk ones favour anyone who has to
        // close. One fixed number cannot describe a mutator that swings both ways,
        // and the +/-5pp cap exists to protect people betting on a specific
        // character. Promote it when there is measured data rather than this guess;
        // until then it is a gym and casual toy, which is where it is fun anyway.
        archetypeDeltaPct: delta({ artillery: 4, assassin: -3, juggernaut: -1 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: "size changes hit probability at range and the sign flips with the roll — unmeasured",
        build: (rng) => ({
            kind: "player_scale",
            // Tamer than the sim's 70-130 bound: the extremes are a gym toy, and a
            // body near either end stops fitting the cover the maps are built around.
            scalePct: rng.int(80, 125),
            durationMs: rng.int(3, 6) * 5_000,
        }),
    },
    lava_floor: {
        kind: "lava_floor",
        tier: "mutator",
        label: "LAVA FLOOR",
        description: "The floor burns. Casual lobbies only.",
        // Symmetric in application, wildly asymmetric in effect: near-fatal to a
        // 0.85x-speed Monolith, close to irrelevant to a Talon whose Perch hovers
        // over it. GAME-DESIGN §4 uses this exact pair as the worked example.
        archetypeDeltaPct: delta({ juggernaut: -9, artillery: 7, assassin: 3 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: "juggernaut -9pp / artillery +7pp — outside the +/-5pp cap",
        build: (rng) => ({
            kind: "lava_floor",
            dps: rng.int(6, 13),
            durationMs: rng.int(4, 8) * 5_000,
        }),
    },
    cooldown_haste: {
        kind: "cooldown_haste",
        tier: "mutator",
        label: "OVERFLOW",
        description: "Every cooldown in flight jumps forward. Casual lobbies only.",
        // Multiplies ability-spam archetypes and devalues Hex, who is deliberately
        // balanced around the lowest damage in the roster.
        archetypeDeltaPct: delta({ controller: -7, assassin: 4, artillery: 5 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: "controller -7pp — outside the +/-5pp cap",
        build: (rng) => ({
            kind: "cooldown_haste",
            hastePct: rng.int(30, 51),
        }),
    },
    damage_amp: {
        kind: "damage_amp",
        tier: "mutator",
        label: "DOUBLE DAMAGE",
        description: "Cut. Fires in no lobby, wagered or casual.",
        // Turns Monolith's 55 ultimate into a 110 one-shot on 80-HP Volt, which
        // destroys the "TTK 6-10s, rarely a solo kill from full HP" pillar. PRD §8
        // cuts this one by name — and the simulation has no damage multiplier for it
        // to set, so re-adding it here would fail at derivation rather than quietly
        // ship.
        archetypeDeltaPct: delta({ juggernaut: 12, assassin: -8 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: EXCLUDED_MUTATION_REASONS.damage_amp,
        build: (rng) => ({
            kind: "damage_amp",
            ampPct: rng.int(150, 201),
            durationMs: rng.int(3, 6) * 5_000,
        }),
    },
    wind_shear: {
        kind: "wind_shear",
        tier: "mutator",
        label: "WIND SHEAR",
        description: "A steady crosswind. Not implementable yet — see the reason.",
        archetypeDeltaPct: delta({ artillery: -4, trickster: 1, juggernaut: 2 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: EXCLUDED_MUTATION_REASONS.wind_shear,
        build: (rng) => ({
            kind: "wind_shear",
            directionDeg: rng.int(0, 360),
            force: rng.float(2, 5),
            durationMs: rng.int(5, 9) * 5_000,
        }),
    },
    health_surge: {
        kind: "health_surge",
        tier: "mutator",
        label: "SUPPLY DROP",
        description: "Health pickups land in the contested centre. Not implementable yet.",
        archetypeDeltaPct: delta({ assassin: 2, juggernaut: -1 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: EXCLUDED_MUTATION_REASONS.health_surge,
        // Centre-only by construction: the radius is a fraction of the arena and the
        // drop is always origin-centred. Peripheral health would subsidise hiding,
        // which is the exact failure GAME-DESIGN §2 is built to avoid.
        build: (rng, ctx) => ({
            kind: "health_surge",
            count: rng.int(2, 5),
            radius: Math.min(40, Math.max(1, ctx.arenaRadius * 0.22)),
        }),
    },
    // --- spectacle ------------------------------------------------------------
    //
    // Both spectacles are capability-excluded, so `WAGERED_SPECTACLE_POOL` is
    // empty and `deriveChaosSchedule` schedules none. The tier machinery stays —
    // budget, announcement, endgame freeze — so that landing a spectacle is a
    // matter of teaching the sim to express one, not of re-deriving the director.
    boss_spawn: {
        kind: "boss_spawn",
        tier: "spectacle",
        label: "THE WARDEN",
        description: "A boss lands at a seeded point and chases whoever is nearest to that point when it lands.",
        archetypeDeltaPct: delta({ juggernaut: 2, artillery: -2, assassin: -1 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: EXCLUDED_MUTATION_REASONS.boss_spawn,
        build: (rng, ctx) => {
            const p = rng.pointInDisc(ctx.arenaRadius * 0.55);
            return {
                kind: "boss_spawn",
                x: round4(p.x),
                z: round4(p.z),
                health: rng.int(6, 13) * 100,
                // The one aggro rule there is, and it is committed in the seed. Anything
                // that picks a target at runtime is targeting with extra steps.
                aggro: "nearest_to_spawn_point",
            };
        },
    },
    sector_collapse: {
        kind: "sector_collapse",
        tier: "spectacle",
        label: "COLLAPSE",
        description: "One wedge of the arena falls away. Not implementable yet.",
        archetypeDeltaPct: delta({ trickster: 2, juggernaut: -3 }),
        deltaSource: "estimate",
        wageredLegal: false,
        excludedReason: EXCLUDED_MUTATION_REASONS.sector_collapse,
        build: (rng) => {
            const sectorCount = 8;
            return {
                kind: "sector_collapse",
                sectorIndex: rng.int(0, sectorCount),
                sectorCount,
                warnMs: 5_000,
            };
        },
    },
    // --- ambient: cosmetic only, zero mechanical effect ------------------------
    fog_roll: {
        kind: "fog_roll",
        tier: "ambient",
        label: "FOG",
        description: "Fog rolls in. Cosmetic.",
        archetypeDeltaPct: flat(0),
        deltaSource: "measured",
        wageredLegal: true,
        build: (rng) => ({
            kind: "fog_roll",
            densityPct: rng.int(30, 71),
            durationMs: rng.int(3, 7) * 5_000,
        }),
    },
    color_shift: {
        kind: "color_shift",
        tier: "ambient",
        label: "SHIFT",
        description: "The arena changes colour. Cosmetic.",
        archetypeDeltaPct: flat(0),
        deltaSource: "measured",
        wageredLegal: true,
        build: (rng) => ({
            kind: "color_shift",
            hueDeg: rng.int(0, 360),
            durationMs: rng.int(3, 7) * 5_000,
        }),
    },
    tremor: {
        kind: "tremor",
        tier: "ambient",
        label: "TREMOR",
        description: "The ground shakes. Camera only — no forces are applied.",
        archetypeDeltaPct: flat(0),
        deltaSource: "measured",
        wageredLegal: true,
        build: (rng) => ({
            kind: "tremor",
            magnitudePct: rng.int(20, 61),
            durationMs: rng.int(1, 4) * 2_000,
        }),
    },
};
function round4(v) {
    return Math.round(v * 10_000) / 10_000;
}
export function maxAbsDelta(spec) {
    return Object.values(spec.archetypeDeltaPct).reduce((m, v) => Math.max(m, Math.abs(v)), 0);
}
function specsByTier(tier, wageredOnly) {
    return Object.values(MUTATOR_SPECS)
        .filter((s) => s.tier === tier && (!wageredOnly || s.wageredLegal))
        .map((s) => s.kind);
}
/**
 * The three pools a wagered match may draw from. These are constants, not a
 * per-match roll: "fixed and public" is the property that lets a player price
 * archetype risk at pick time.
 */
export const WAGERED_MUTATOR_POOL = Object.freeze(specsByTier("mutator", true));
export const WAGERED_SPECTACLE_POOL = Object.freeze(specsByTier("spectacle", true));
export const WAGERED_AMBIENT_POOL = Object.freeze(specsByTier("ambient", true));
export function publishedWageredPool() {
    return [...WAGERED_MUTATOR_POOL, ...WAGERED_SPECTACLE_POOL, ...WAGERED_AMBIENT_POOL].map((kind) => {
        const spec = MUTATOR_SPECS[kind];
        return {
            kind: spec.kind,
            tier: spec.tier,
            label: spec.label,
            description: spec.description,
            archetypeDeltaPct: spec.archetypeDeltaPct,
            deltaSource: spec.deltaSource,
        };
    });
}
export class PoolBalanceError extends Error {
    constructor(message) {
        super(message);
        this.name = "PoolBalanceError";
    }
}
/**
 * Fail at boot, not at the first payout.
 *
 * A balance pass that widens a mutator past the cap without removing it from the
 * wagered pool is exactly the kind of change that ships quietly and is only
 * noticed in the win-rate data three weeks later.
 */
function assertPoolWithinCap() {
    for (const kind of [...WAGERED_MUTATOR_POOL, ...WAGERED_SPECTACLE_POOL, ...WAGERED_AMBIENT_POOL]) {
        const spec = MUTATOR_SPECS[kind];
        const worst = maxAbsDelta(spec);
        if (worst > MAX_ABS_ARCHETYPE_DELTA_PCT) {
            throw new PoolBalanceError(`${kind} is in the wagered pool with a ${worst}pp archetype delta ` +
                `(cap ${MAX_ABS_ARCHETYPE_DELTA_PCT}pp). Set wageredLegal:false or retune it.`);
        }
        if (spec.excludedReason !== undefined) {
            throw new PoolBalanceError(`${kind} is wageredLegal but carries an excludedReason`);
        }
        if (!isApplicableMutationKind(kind)) {
            // A published pool entry the simulation cannot apply is worse than a
            // missing one: players are shown it before character select and price
            // archetype risk against something that will never happen.
            throw new PoolBalanceError(`${kind} is in the wagered pool but the simulation cannot apply it. ` +
                `Add it to ChaosMutation and to @chaos/sim, or set wageredLegal:false.`);
        }
    }
}
assertPoolWithinCap();
/**
 * Build one mutation from a spec, validating the result against the same runtime
 * allowlist an LLM-authored payload would face. A spec with a typo'd range is a
 * bug we want to hit here, in a pure function, not three layers into the sim.
 */
export function buildMutation(kind, rng, ctx) {
    const spec = MUTATOR_SPECS[kind];
    const mutation = spec.build(rng, ctx);
    assertChaosMutation(mutation);
    return mutation;
}
//# sourceMappingURL=pool.js.map